import httpService from "./http.service";
import CrudService from "./cruds-service";
import deviceInstallationService, { installationMessage } from "./device-installation-service";

export { installationMessage };

function authHeaders(sessionToken) {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(sessionToken ? { "X-Onboarding-Session-Token": sessionToken } : {}),
  };
}

// Normalise the three response envelopes used across Rivio:
// { success, data }, { data }, and bare objects such as { address }.
export function unwrap(body, fallbackKey) {
  if (body == null) throw new Error("Empty API response");
  if (body.success === true && body.data !== undefined) return body.data;
  if (body.data !== undefined) return body.data;
  if (fallbackKey && body[fallbackKey] !== undefined) return body[fallbackKey];
  if (body.success === false) throw new Error(body.message || body.error || "Request failed");
  return body;
}

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.data?.data)) return value.data.data;
  return [];
}

const thresholdKey = (row) =>
  [row.parameter_id, row.threshold_type_id, row.alert_type_id, row.comparison_operator_id]
    .map((v) => (v == null ? "" : String(v)))
    .join(":");

const api = {
  // ---- installation session (device-installation-service passthrough) ----
  routing: (code) => deviceInstallationService.routing(code),
  start: (code) => deviceInstallationService.start(code),
  session: (session) => deviceInstallationService.session(session),
  overview: (session) => deviceInstallationService.overview(session),
  targets: (session, entityId) => deviceInstallationService.targets(session, entityId),
  saveStep: (session, stepKey, payload) => deviceInstallationService.save(session, stepKey, payload),
  complete: (session, payload) => deviceInstallationService.complete(session, payload),
  status: (claimId) => deviceInstallationService.status(claimId),
  claimSetupOptions: (session, target) => deviceInstallationService.claimSetupOptions(session, target),
  saveSetup: (session, body) => deviceInstallationService.saveSetup(session, body),
  applyOperations: (claimId, operations) => deviceInstallationService.applyOperations(claimId, operations),
  summary: (claimId) => deviceInstallationService.summary(claimId),

  // ---- customers ----
  searchCustomers: async () => {
    const res = await CrudService.getEntitiesList();
    return asArray(res);
  },
  createCustomer: async (payload) => {
    const res = await CrudService.onboardingCustomer(payload);
    const data = unwrap(res);
    const id = data?.id ?? data?.entity_id ?? data?.data?.id;
    if (!id) throw new Error("Customer created but no entity id was returned");
    return { entity_id: Number(id), raw: data };
  },

  // ---- addresses ----
  // Limitation: this endpoint derives addresses from asset rows, so a linked
  // address with no asset can be missing. "Add a new service address" is
  // always offered so the installer can recover.
  listAddresses: async (entityId) => {
    const res = await CrudService.getAddressListByEntity(entityId);
    return asArray(res);
  },
  createAddress: async (payload) => {
    const res = await CrudService.addAddressToEntity(payload);
    const data = unwrap(res, "address");
    const id = data?.address_id ?? data?.id ?? data?.Address?.address_id;
    if (!id) throw new Error("Address saved but no address id was returned");
    return { address_id: Number(id), raw: data };
  },

  // ---- assets ----
  // Limitation: provider_ids is accepted by this endpoint but not persisted;
  // provider sharing is applied afterwards with the per-asset toggle.
  createAsset: async (payload) => {
    const res = await CrudService.createEntityAddressItem(payload);
    const data = unwrap(res);
    const id =
      data?.address_item_id ??
      data?.id ??
      data?.data?.address_item_id ??
      (Array.isArray(data) ? data[0]?.address_item_id : undefined);
    if (!id) throw new Error("Asset saved but no asset id was returned");
    return { address_item_id: Number(id), raw: data };
  },

  // ---- contacts ----
  listContacts: async (entityId) => {
    const res = await CrudService.getAllContactsByEntity(entityId);
    return asArray(res);
  },
  createContact: async (payload) => {
    const res = await CrudService.createContact(payload);
    const data = unwrap(res);
    const id = data?.contact_id ?? data?.id ?? data?.data?.contact_id ?? data?.data?.id;
    return { contact_id: id ? Number(id) : null, raw: data };
  },

  // ---- provider sharing ----
  shareOptions: async () => {
    const res = await CrudService.getProviderShareOptions();
    return asArray(res).filter((p) => p.can_share === true);
  },
  setProviderShare: async (assetId, providerId, enabled) => {
    const res = await httpService.put(
      `entities/entity-address-items/${encodeURIComponent(assetId)}/providers/${encodeURIComponent(providerId)}`,
      { enabled: Boolean(enabled) },
      { headers: { "Content-Type": "application/json" } },
    );
    return unwrap(res, "data");
  },

  // ---- level-sensor calibration (interim direct path) ----
  // Replaces the four editable calibration fields for one internal device ID.
  // Uses the current signed-in user's bearer token (no onboarding token); the
  // server derives scale_unit and invert_scale, so neither is ever sent.
  // Rejected errors are usually the response body, not an Axios error.
  saveLevelCalibration: async (deviceId, payload) => {
    if (!Number.isSafeInteger(Number(deviceId)) || Number(deviceId) <= 0) {
      throw new Error("A valid internal device ID is required to save calibration.");
    }
    const body = await httpService.put(
      `level-devices/${encodeURIComponent(deviceId)}/configuration`,
      payload,
      // A 403 here means this account lacks the device write grant; the
      // wizard shows that failure with its own retry instead of being
      // logged out as if the session had expired.
      { headers: authHeaders(), skipLogoutOn403: true },
    );
    if (body?.success !== true || !body.data) {
      throw new Error(body?.message || "Calibration save returned an invalid response.");
    }
    return body.data;
  },

  // ---- optional installation device test (proposed backend contract) ----
  // Usable only once the server advertises installation_device_test. The
  // session identifies the claimed device; no device ID is ever sent, and
  // tokens stay in headers - never in URLs, room names or event bodies.
  startDeviceTest: async (session, draft, signal) => unwrap(await httpService.post(
    `onboarding/session/${encodeURIComponent(session.session_id)}/installation/device-tests`,
    draft,
    { headers: authHeaders(session.session_token), signal },
  )),
  deviceTest: async (session, testId, signal) => unwrap(await httpService.get(
    `onboarding/session/${encodeURIComponent(session.session_id)}/installation/device-tests/${encodeURIComponent(testId)}`,
    { headers: authHeaders(session.session_token), signal },
  )),
  cancelDeviceTest: async (session, testId, signal) => unwrap(await httpService.post(
    `onboarding/session/${encodeURIComponent(session.session_id)}/installation/device-tests/${encodeURIComponent(testId)}/cancel`,
    {},
    { headers: authHeaders(session.session_token), signal },
  )),

  // ---- lookups / dropdowns ----
  dropdowns: async () => {
    const res = await CrudService.onboardingDropData();
    return unwrap(res) || {};
  },
  loadAlertLookups: async (deviceId) => {
    const res = await CrudService.loadAlertRuleLookup(deviceId);
    return unwrap(res) || res || {};
  },
  alertRuleList: async (deviceId) => {
    const res = await httpService.post(
      "alertrules/list",
      { id: deviceId },
      { headers: { "Content-Type": "application/json" } },
    );
    return asArray(res);
  },
  createAlertRule: async (payload) => {
    const res = await httpService.post("alertrules", payload, {
      headers: { "Content-Type": "application/json" },
    });
    return unwrap(res);
  },

  // ---- threshold metadata join ----
  // The installation overview returns raw threshold IDs with no labels; join
  // parameter/type/operator metadata client-side so the field copy is usable.
  loadThresholdLookups: async () => {
    const [params, types, operators] = await Promise.all([
      CrudService.getDeviceParameters().catch(() => []),
      CrudService.getThresholdTypes().catch(() => []),
      CrudService.getComparisonOperators().catch(() => []),
    ]);
    const parameterMap = new Map();
    asArray(params).forEach((row) => {
      const id = row.parameter_id ?? row.id ?? row?.DeviceParameter?.parameter_id;
      if (id != null) parameterMap.set(Number(id), row.label ?? row.name ?? row?.DeviceParameter?.label ?? `Parameter ${id}`);
    });
    const typeMap = new Map();
    asArray(types).forEach((row) => {
      const id = row.threshold_type_id ?? row.id;
      if (id != null) typeMap.set(Number(id), row.label ?? row.name ?? row.type_name ?? "");
    });
    const operatorMap = new Map();
    asArray(operators).forEach((row) => {
      const id = row.comparison_operator_id ?? row.id;
      if (id != null) operatorMap.set(Number(id), row.label ?? row.symbol ?? row.name ?? "");
    });
    return { parameterMap, typeMap, operatorMap };
  },
  joinThresholdLabel: (definition, lookups) => {
    const base =
      lookups?.parameterMap?.get(Number(definition.parameter_id)) ||
      definition?.DeviceParameter?.label ||
      `Parameter ${definition.parameter_id}`;
    const type = lookups?.typeMap?.get(Number(definition.threshold_type_id));
    const op = lookups?.operatorMap?.get(Number(definition.comparison_operator_id));
    return [base, op, type].filter(Boolean).join(" ");
  },

  // ---- reverse geocode (client-side provider call) ----
  // Limitation: no server-side reverse-geocode endpoint exists yet. Uses the
  // Google Maps JS/Geocoding key directly and degrades to manual entry.
  reverseGeocode: async (latitude, longitude) => {
    const key = process.env.REACT_APP_MAP_API_KEY;
    if (!key) return { supported: false };
    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${encodeURIComponent(
      latitude,
    )},${encodeURIComponent(longitude)}&key=${encodeURIComponent(key)}`;
    const res = await fetch(url);
    const body = await res.json();
    if (body.status !== "OK" || !body.results?.length) {
      return { supported: true, found: false };
    }
    const result = body.results[0];
    const get = (type) => result.address_components?.find((c) => c.types.includes(type))?.long_name || "";
    return {
      supported: true,
      found: true,
      formatted_address: result.formatted_address,
      street_address1: [get("street_number"), get("route")].filter(Boolean).join(" "),
      city: get("locality") || get("administrative_area_level_2"),
      state: get("administrative_area_level_1"),
      postal_code: get("postal_code"),
      country: get("country"),
      latitude,
      longitude,
    };
  },
};

export default api;
